import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Loader } from '@progress/kendo-react-indicators';
import { debounce } from 'lodash';
import style from './style.module.scss';
import AutomationStatusIcon from '@components-clean/core/grid/grid-status/status-icon';
import { AutomationStatuses } from '@components-clean/core/grid/grid-status/types';
import styles from '@components-clean/core/metadata/style.module.scss';
import Tooltip from '@components-clean/core/tooltip';
import { AutomationStatusSetupProps, ObservationType } from '@interfaces-clean/observations.interface';
import { AutomationTypes } from '@pages-clean/central-monitoring/dataset-hub/interfaces/hub-dataset.interface';
import { useBoolean, useLocalState } from '@hooks/index';
import { stringDateWithTimeFromUtcNonNullable } from '@utils/convertor';
import { GET_STUDY_AUTOMATION_CONFIG } from '@queries/central-monitoring';
import {
    getStudyAutomationConfig,
    getStudyAutomationConfigVariables,
} from '@queries/generated/central-monitoring.generated';
import AutomationApi from '@apis/bff-api/central-monitoring/automation';
import ObservationsApi from '@apis/bff-api/central-monitoring/observation';

export interface IntegrationStatusSetupProps {
    studyRef: string;
    observationType: ObservationType;
}

export interface GridStatusProps {
    automationStatusSetup?: AutomationStatusSetupProps;
    integrationStatusSetup?: IntegrationStatusSetupProps;
}

const AutomationStatus = ({ automationStatusSetup }: { automationStatusSetup?: AutomationStatusSetupProps }) => {
    if (!automationStatusSetup) return null;

    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    /* eslint-disable @eslint-react/rules-of-hooks */
    const { t } = useTranslation('cm');
    const [status, setStatus] = useState<AutomationStatuses | undefined>(undefined);
    const [displayStatus, setDisplayStatus] = useState<string | undefined>(undefined);
    const [datasetName, setDatasetName] = useState<string | undefined>(undefined);
    const [tooltipText, setTooltipText] = useState<string | undefined>(undefined);
    const [hideAutomationStatus, setHideAutomationStatus] = useState<boolean>(false);

    // are automations enabled for this study?
    const {
        data: automationConfig,
        error: automationConfigError,
        loading: isLoadingConfig,
    } = useQuery<getStudyAutomationConfig, getStudyAutomationConfigVariables>(GET_STUDY_AUTOMATION_CONFIG, {
        variables: { studyId: automationStatusSetup.studyRef },
        fetchPolicy: 'no-cache',
    });

    const isAutomationEnabled =
        automationStatusSetup.automationType === AutomationTypes.Site
            ? (automationConfig?.getStudyAutomationConfig?.isAutomationEnabled ?? null)
            : (automationConfig?.getStudyAutomationConfig?.isControlsAutomationEnabled ?? null);

    // get automation status
    const { data: statusData, isLoading: isLoadingStatus } = AutomationApi.useGetStatus(
        automationStatusSetup.studyRef,
        automationConfig ? isAutomationEnabled : null,
        automationStatusSetup.automationType,
    );

    // translations
    const notRun = t('cm:monitoringToolkit.automationStatus.notRun');
    const noAutomationsExistForThisStudy =
        automationStatusSetup.automationType === AutomationTypes.Site
            ? t('cm:monitoringToolkit.automationStatus.noAutomationsExistForThisStudy')
            : t('cm:monitoringToolkit.automationStatus.noControlsAutomationsExistForThisStudy');

    const latestAutomation =
        automationStatusSetup.automationType === AutomationTypes.Site
            ? t('cm:monitoringToolkit.automationStatus.latestAutomation')
            : t('cm:monitoringToolkit.automationStatus.latestControlsAutomation');
    const latestAutomationSuccess = t('cm:monitoringToolkit.automationStatus.latestAutomationSuccess');
    const latestAutomationFail = t('cm:monitoringToolkit.automationStatus.latestAutomationFail');

    useEffect(() => {
        // reset status and tooltip text
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        /* eslint-disable @eslint-react/set-state-in-effect */
        setStatus(undefined);
        setTooltipText(undefined);
        setDatasetName(undefined);
        setDisplayStatus(undefined);
        setHideAutomationStatus(true);
        setHideAutomationStatus(true);

        if (
            // basic checks
            isLoadingConfig ||
            isLoadingStatus ||
            automationConfigError ||
            !automationConfig ||
            // automations disabled, no automations - clear everything
            (!isAutomationEnabled && (!statusData || statusData.status === 'Not Run'))
        ) {
            return;
        }

        setHideAutomationStatus(false);

        // automations enabled and... no automations / automations running / automations reverting: show not run status
        if (
            isAutomationEnabled &&
            (statusData?.status === 'Not Run' || statusData?.status === 'Running' || statusData?.status === 'Reverting')
        ) {
            setStatus('Not Run');
            setDisplayStatus(notRun);
            setDatasetName(undefined);
            setTooltipText(noAutomationsExistForThisStudy);
            return;
        }

        // automations enabled / disabled - get latest status (successful or failed)
        if (statusData) {
            setDatasetName(statusData.datasetName);

            // latest automation status successful
            if (statusData.status === 'Successful') {
                setStatus('Success');
                setTooltipText(`${latestAutomation} ${statusData.datasetName} ${latestAutomationSuccess}`);
                return;
            }

            // latest automation status failed
            if (statusData.status === 'Failed') {
                setStatus('Failed');
                setTooltipText(`${latestAutomation} ${statusData.datasetName} ${latestAutomationFail}`);
            }
        }
        /* eslint-enable @eslint-react/set-state-in-effect */
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [automationConfig, statusData]);
    /* eslint-enable @eslint-react/rules-of-hooks */

    return (
        !isLoadingStatus &&
        !isLoadingConfig &&
        status &&
        tooltipText &&
        !hideAutomationStatus && (
            <div className={style.statusDiv}>
                <span className={style.spacer}>|</span>
                <Tooltip position="bottom" isInactive={false} tooltipClassName={style.tooltip}>
                    <FontAwesomeIcon aria-label={tooltipText} icon={['far', 'gear-code']} className={style.gearIcon} />
                </Tooltip>
                <Tooltip position="bottom" isInactive={false} tooltipClassName={style.tooltip}>
                    <span title={tooltipText} className="k-pr-2">
                        {datasetName} {status === 'Not Run' ? displayStatus : ''}
                    </span>
                </Tooltip>
                <AutomationStatusIcon status={status} testId="automation-status-icon" toolTipText={tooltipText} />
                <span className={style.spacer}>|</span>
            </div>
        )
    );
};

const GridTooltipStatus = ({
    automationStatusSetup,
    integrationStatusSetup,
}: {
    automationStatusSetup: AutomationStatusSetupProps;
    integrationStatusSetup: IntegrationStatusSetupProps;
}) => {
    const { t } = useTranslation();

    const {
        data: automationConfig,
        error: automationConfigError,
        loading: isLoadingConfig,
    } = useQuery<getStudyAutomationConfig, getStudyAutomationConfigVariables>(GET_STUDY_AUTOMATION_CONFIG, {
        variables: { studyId: automationStatusSetup.studyRef },
        fetchPolicy: 'no-cache',
    });

    const { state } = useLocalState();

    const isAutomationEnabled =
        automationStatusSetup.automationType === AutomationTypes.Site
            ? (automationConfig?.getStudyAutomationConfig?.isAutomationEnabled ?? null)
            : (automationConfig?.getStudyAutomationConfig?.isControlsAutomationEnabled ?? null);

    // get automation status
    const {
        data: automationData,
        isValidating: loadingAutomation,
        mutate: refetchAutomation,
    } = AutomationApi.useGetStatus(
        automationStatusSetup.studyRef,
        automationConfig ? isAutomationEnabled : null,
        automationStatusSetup.automationType,
    );

    const {
        data: integrationData,
        mutate: refetchIntegration,
        isValidating: loadingIntegration,
    } = ObservationsApi.GetIntegrationStatus(integrationStatusSetup.studyRef, integrationStatusSetup.observationType);

    const integrationActive = state?.selectedStudy?.integrationActive;
    const isWaitingLoading = useBoolean(true);

    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    const debouncedRefetch = useCallback(
        debounce(async () => {
            isAutomationEnabled && (await refetchAutomation());
            integrationActive && (await refetchIntegration());
            isWaitingLoading.setFalse();
        }, 1500),
        [],
    );

    const tooltipOpen = async () => {
        // In here setup the debounce to refetch statuses
        isWaitingLoading.setTrue();
        await debouncedRefetch();
    };

    const tooltipClose = () => {
        debouncedRefetch.cancel();
    };

    const tooltipContent = () => (
        <div>
            {automationConfig && automationConfig.getStudyAutomationConfig.isAutomationEnabled && automationData && (
                <div>
                    {t('cm:monitoringToolkit.automationStatus.latestAutomation')}{' '}
                    {loadingAutomation || isWaitingLoading.value ? (
                        <Loader themeColor="light" size="small" />
                    ) : (
                        <span>
                            {automationData.datasetName} {automationData.status}
                        </span>
                    )}
                </div>
            )}
            {integrationActive === true && (
                <div>
                    {t('cm:monitoringToolkit.integrationStatus.latestIntegration')}{' '}
                    {loadingIntegration || isWaitingLoading.value ? (
                        <Loader themeColor="light" size="small" />
                    ) : (
                        <span>
                            {integrationData
                                ? stringDateWithTimeFromUtcNonNullable(
                                      new Date(integrationData),
                                      'YYYY-MMM-DD HH:mm:ss',
                                  )
                                : t('cm:monitoringToolkit.integrationStatus.notRun')}
                        </span>
                    )}
                </div>
            )}
        </div>
    );

    if (isLoadingConfig || automationConfigError || !automationConfig) return;
    if (automationConfig && !automationConfig.getStudyAutomationConfig.isAutomationEnabled && !integrationActive)
        return;

    return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div className={style.statusDiv} onMouseEnter={tooltipOpen} onMouseLeave={tooltipClose}>
            <Tooltip
                position="bottom"
                data-testid="popover-datatestid"
                content={tooltipContent}
                anchorElement="target"
                parentTitle
                openDelay={0}
            >
                <div title="status" className={styles.metadataBtn}>
                    <FontAwesomeIcon
                        fontSize={16}
                        icon={['far', 'clock-rotate-left']}
                        className={styles.metaDataIcon}
                    />
                </div>
            </Tooltip>
            <span className="k-pl-1.5">|</span>
        </div>
    );
};

const GridStatusBar = ({ automationStatusSetup, integrationStatusSetup }: GridStatusProps) => {
    if (automationStatusSetup && !integrationStatusSetup)
        return <AutomationStatus automationStatusSetup={automationStatusSetup} />;

    if (automationStatusSetup && integrationStatusSetup)
        return (
            <GridTooltipStatus
                automationStatusSetup={automationStatusSetup}
                integrationStatusSetup={integrationStatusSetup}
            />
        );

    return <div>Nothing to show here</div>;
};

export default GridStatusBar;
